import React, { useEffect, useState, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Spinner,
  Badge,
  Button,
  Form,
  ListGroup,
  InputGroup,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import SubtleBadge from "components/common/SubtleBadge";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import { TagManager } from "components/general/tags";
import { useAuth } from "contexts/AuthContext";
import paths from "routes/paths";
import * as pmService from "services/pmService";

dayjs.extend(relativeTime);

const STATUS_CONFIG = {
  OPEN: { bg: "primary", label: "Open" },
  IN_PROGRESS: { bg: "warning", label: "In Progress" },
  COMPLETED: { bg: "success", label: "Completed" },
  BLOCKED: { bg: "danger", label: "Blocked" },
};

const PRIORITY_MAP = {
  0: { label: "None", bg: "secondary" },
  1: { label: "Low", bg: "info" },
  2: { label: "Medium", bg: "warning" },
  3: { label: "High", bg: "danger" },
  4: { label: "Critical", bg: "danger" },
};

const TaskDetailPage = () => {
  const navigate = useNavigate();
  const { projectId, iterationId, taskId } = useParams();
  const { user } = useAuth();

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [links, setLinks] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [linksLoading, setLinksLoading] = useState(false);

  // Comment form
  const [newComment, setNewComment] = useState("");
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentContent, setEditCommentContent] = useState("");

  // Link form
  const [newLinkUrl, setNewLinkUrl] = useState("");
  const [newLinkLabel, setNewLinkLabel] = useState("");
  const [linkSubmitting, setLinkSubmitting] = useState(false);
  const [editingLinkId, setEditingLinkId] = useState(null);
  const [editLinkUrl, setEditLinkUrl] = useState("");
  const [editLinkLabel, setEditLinkLabel] = useState("");

  // Delete modals
  const [deleteCommentModal, setDeleteCommentModal] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [deleteLinkModal, setDeleteLinkModal] = useState(false);
  const [linkToDelete, setLinkToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const userName = user
    ? `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
      user.email ||
      user.username
    : "";

  // Load task data
  const loadTask = useCallback(async () => {
    try {
      setLoading(true);
      const data = await pmService.getTask(projectId, taskId);
      setTask(data);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load task");
    } finally {
      setLoading(false);
    }
  }, [projectId, taskId]);

  // Load comments
  const loadComments = useCallback(async () => {
    try {
      setCommentsLoading(true);
      const data = await pmService.listComments(projectId, taskId);
      setComments(Array.isArray(data) ? data : data.content || []);
    } catch (err) {
      console.error("Failed to load comments:", err);
    } finally {
      setCommentsLoading(false);
    }
  }, [projectId, taskId]);

  // Load links
  const loadLinks = useCallback(async () => {
    try {
      setLinksLoading(true);
      const data = await pmService.listLinks(taskId);
      setLinks(data);
    } catch (err) {
      console.error("Failed to load links:", err);
    } finally {
      setLinksLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    if (projectId && taskId) {
      loadTask();
      loadComments();
      loadLinks();
    }
  }, [projectId, taskId, loadTask, loadComments, loadLinks]);

  // ─── Comment handlers ───────────────────────────────────────────────
  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      setCommentSubmitting(true);
      const created = await pmService.addComment(
        projectId,
        taskId,
        { content: newComment },
        user?.id,
        userName
      );
      setComments((prev) => [created, ...prev]);
      setNewComment("");
      toast.success("Comment added");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add comment");
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleUpdateComment = async (commentId) => {
    if (!editCommentContent.trim()) return;
    try {
      const updated = await pmService.updateComment(
        projectId,
        taskId,
        commentId,
        { content: editCommentContent },
        user?.id,
        userName
      );
      setComments((prev) =>
        prev.map((c) => (c.id === commentId ? updated : c))
      );
      setEditingCommentId(null);
      setEditCommentContent("");
      toast.success("Comment updated");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update comment");
    }
  };

  const handleDeleteComment = async () => {
    if (!commentToDelete) return;
    try {
      setDeleteLoading(true);
      await pmService.deleteComment(projectId, taskId, commentToDelete);
      setComments((prev) => prev.filter((c) => c.id !== commentToDelete));
      toast.success("Comment deleted");
    } catch (err) {
      toast.error("Failed to delete comment");
    } finally {
      setDeleteLoading(false);
      setDeleteCommentModal(false);
      setCommentToDelete(null);
    }
  };

  // ─── Link handlers ──────────────────────────────────────────────────
  const handleAddLink = async (e) => {
    e.preventDefault();
    if (!newLinkUrl.trim()) return;
    try {
      setLinkSubmitting(true);
      const created = await pmService.addLinks(
        taskId,
        [{ url: newLinkUrl, label: newLinkLabel || null }],
        user?.id
      );
      const newLinks = Array.isArray(created) ? created : [created];
      setLinks((prev) => [...prev, ...newLinks]);
      setNewLinkUrl("");
      setNewLinkLabel("");
      toast.success("Link added");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add link");
    } finally {
      setLinkSubmitting(false);
    }
  };

  const handleUpdateLink = async (linkId) => {
    if (!editLinkUrl.trim()) return;
    try {
      const updated = await pmService.updateLink(
        taskId,
        linkId,
        { url: editLinkUrl, label: editLinkLabel || null },
        user?.id
      );
      setLinks((prev) => prev.map((l) => (l.id === linkId ? updated : l)));
      setEditingLinkId(null);
      toast.success("Link updated");
    } catch (err) {
      toast.error("Failed to update link");
    }
  };

  const handleDeleteLink = async () => {
    if (!linkToDelete) return;
    try {
      setDeleteLoading(true);
      await pmService.deleteLink(taskId, linkToDelete, user?.id);
      setLinks((prev) => prev.filter((l) => l.id !== linkToDelete));
      toast.success("Link deleted");
    } catch (err) {
      toast.error("Failed to delete link");
    } finally {
      setDeleteLoading(false);
      setDeleteLinkModal(false);
      setLinkToDelete(null);
    }
  };

  // ─── Tag change handler ─────────────────────────────────────────────
  const handleTagsChange = (newTags) => {
    setTask((prev) => (prev ? { ...prev, tags: newTags } : prev));
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Loading task...</span>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="text-center py-5">
        <h5 className="text-500">Task not found</h5>
        <Button
          variant="falcon-primary"
          size="sm"
          onClick={() => navigate(-1)}
        >
          Go Back
        </Button>
      </div>
    );
  }

  const statusCfg = STATUS_CONFIG[task.status] || {};
  const priorityCfg = PRIORITY_MAP[task.priority] || PRIORITY_MAP[0];

  return (
    <>
      <WidgetSectionTitle
        icon="clipboard-check"
        title={task.title}
        subtitle={`Task in project`}
        transform="shrink-2"
        className="mb-4 mt-3"
      />

      {/* Breadcrumb */}
      <div className="mb-3">
        <small className="text-500">
          <span
            className="text-primary cursor-pointer"
            style={{ cursor: "pointer" }}
            onClick={() => navigate(paths.projects)}
          >
            Projects
          </span>
          {" / "}
          <span
            className="text-primary"
            style={{ cursor: "pointer" }}
            onClick={() => navigate(paths.projectDetails(projectId))}
          >
            Project
          </span>
          {task.iterationId && iterationId && iterationId !== "_" && (
            <>
              {" / "}
              <span
                className="text-primary"
                style={{ cursor: "pointer" }}
                onClick={() =>
                  navigate(
                    paths.projectIterationDetails(projectId, iterationId)
                  )
                }
              >
                Iteration
              </span>
            </>
          )}
          {" / "}
          <span className="text-700">{task.title}</span>
        </small>
      </div>

      <Row className="g-3">
        {/* Main Content */}
        <Col lg={8}>
          {/* Task Info Card */}
          <Card className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Task Details</h6>
              <div>
                <SubtleBadge bg={statusCfg.bg} className="me-2">
                  {statusCfg.label || task.status}
                </SubtleBadge>
                <SubtleBadge bg={priorityCfg.bg}>
                  P{task.priority ?? "—"}: {priorityCfg.label}
                </SubtleBadge>
              </div>
            </Card.Header>
            <Card.Body>
              {task.description && (
                <div className="mb-3">
                  <h6 className="text-600 fs-10">Description</h6>
                  <div
                    className="text-800 fs-10"
                    dangerouslySetInnerHTML={{ __html: task.description }}
                  />
                </div>
              )}

              {task.acceptanceCriteria && (
                <div className="mb-3">
                  <h6 className="text-600 fs-10">Acceptance Criteria</h6>
                  <div
                    className="text-800 fs-10"
                    dangerouslySetInnerHTML={{
                      __html: task.acceptanceCriteria,
                    }}
                  />
                </div>
              )}

              <Row className="g-2 mt-2">
                <Col sm={6} md={4}>
                  <small className="text-500 d-block">Points</small>
                  <span className="fw-semi-bold">{task.points}</span>
                </Col>
                <Col sm={6} md={4}>
                  <small className="text-500 d-block">Assignee</small>
                  <span className="fw-semi-bold">
                    {task.assigneeName || "—"}
                  </span>
                </Col>
                <Col sm={6} md={4}>
                  <small className="text-500 d-block">Created By</small>
                  <span className="fw-semi-bold">
                    {task.createdByName || "—"}
                  </span>
                </Col>
                <Col sm={6} md={4}>
                  <small className="text-500 d-block">Started At</small>
                  <span>{task.startedAt || "—"}</span>
                </Col>
                <Col sm={6} md={4}>
                  <small className="text-500 d-block">Expected Completion</small>
                  <span>{task.expectedCompletionDate || "—"}</span>
                </Col>
                <Col sm={6} md={4}>
                  <small className="text-500 d-block">Actual Completion</small>
                  <span>{task.actualCompletionDate || "—"}</span>
                </Col>
                <Col sm={6} md={4}>
                  <small className="text-500 d-block">Created</small>
                  <span>
                    {task.createdAt
                      ? dayjs(task.createdAt).format("MMM D, YYYY h:mm A")
                      : "—"}
                  </span>
                </Col>
                <Col sm={6} md={4}>
                  <small className="text-500 d-block">Updated</small>
                  <span>
                    {task.updatedAt
                      ? dayjs(task.updatedAt).format("MMM D, YYYY h:mm A")
                      : "—"}
                  </span>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Comments Section */}
          <Card className="mb-3">
            <Card.Header>
              <h6 className="mb-0">
                <FontAwesomeIcon icon="comments" className="me-2" />
                Comments ({comments.length})
              </h6>
            </Card.Header>
            <Card.Body>
              {/* Add Comment Form */}
              <Form onSubmit={handleAddComment} className="mb-3">
                <InputGroup>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    maxLength={8000}
                  />
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={!newComment.trim() || commentSubmitting}
                  >
                    {commentSubmitting ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <FontAwesomeIcon icon="paper-plane" />
                    )}
                  </Button>
                </InputGroup>
              </Form>

              {commentsLoading ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : comments.length === 0 ? (
                <p className="text-500 text-center mb-0">No comments yet</p>
              ) : (
                <ListGroup variant="flush">
                  {comments.map((comment) => (
                    <ListGroup.Item key={comment.id} className="px-0">
                      {editingCommentId === comment.id ? (
                        <div>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={editCommentContent}
                            onChange={(e) =>
                              setEditCommentContent(e.target.value)
                            }
                            maxLength={8000}
                          />
                          <div className="mt-2">
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() =>
                                handleUpdateComment(comment.id)
                              }
                              className="me-2"
                            >
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="falcon-default"
                              onClick={() => setEditingCommentId(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <strong className="fs-10">
                                {comment.createdByName || "Unknown"}
                              </strong>
                              <small className="text-500 ms-2">
                                {dayjs(comment.createdAt).fromNow()}
                              </small>
                            </div>
                            <div>
                              <Button
                                size="sm"
                                variant="link"
                                className="text-500 p-0 me-2"
                                onClick={() => {
                                  setEditingCommentId(comment.id);
                                  setEditCommentContent(comment.content);
                                }}
                              >
                                <FontAwesomeIcon icon="edit" />
                              </Button>
                              <Button
                                size="sm"
                                variant="link"
                                className="text-danger p-0"
                                onClick={() => {
                                  setCommentToDelete(comment.id);
                                  setDeleteCommentModal(true);
                                }}
                              >
                                <FontAwesomeIcon icon="trash-alt" />
                              </Button>
                            </div>
                          </div>
                          <p className="mb-0 mt-1 fs-10 text-800">
                            {comment.content}
                          </p>
                        </div>
                      )}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Sidebar */}
        <Col lg={4}>
          {/* Tags */}
          <Card className="mb-3">
            <Card.Header>
              <h6 className="mb-0">
                <FontAwesomeIcon icon="tags" className="me-2" />
                Tags
              </h6>
            </Card.Header>
            <Card.Body>
              <TagManager
                entityType="task"
                entityId={taskId}
                projectId={projectId}
                currentTags={task.tags || []}
                onTagsChange={handleTagsChange}
              />
            </Card.Body>
          </Card>

          {/* Links */}
          <Card className="mb-3">
            <Card.Header>
              <h6 className="mb-0">
                <FontAwesomeIcon icon="link" className="me-2" />
                Links ({links.length})
              </h6>
            </Card.Header>
            <Card.Body>
              {/* Add Link Form */}
              <Form onSubmit={handleAddLink} className="mb-3">
                <Form.Control
                  size="sm"
                  type="url"
                  placeholder="URL"
                  value={newLinkUrl}
                  onChange={(e) => setNewLinkUrl(e.target.value)}
                  className="mb-1"
                  required
                />
                <Form.Control
                  size="sm"
                  type="text"
                  placeholder="Label (optional)"
                  value={newLinkLabel}
                  onChange={(e) => setNewLinkLabel(e.target.value)}
                  className="mb-1"
                />
                <Button
                  size="sm"
                  variant="falcon-primary"
                  type="submit"
                  disabled={!newLinkUrl.trim() || linkSubmitting}
                >
                  {linkSubmitting ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>
                      <FontAwesomeIcon icon="plus" className="me-1" />
                      Add Link
                    </>
                  )}
                </Button>
              </Form>

              {linksLoading ? (
                <div className="text-center py-2">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : links.length === 0 ? (
                <p className="text-500 text-center mb-0 fs-10">No links</p>
              ) : (
                <ListGroup variant="flush">
                  {links.map((link) => (
                    <ListGroup.Item key={link.id} className="px-0 py-2">
                      {editingLinkId === link.id ? (
                        <div>
                          <Form.Control
                            size="sm"
                            type="url"
                            value={editLinkUrl}
                            onChange={(e) => setEditLinkUrl(e.target.value)}
                            className="mb-1"
                          />
                          <Form.Control
                            size="sm"
                            type="text"
                            placeholder="Label"
                            value={editLinkLabel}
                            onChange={(e) =>
                              setEditLinkLabel(e.target.value)
                            }
                            className="mb-1"
                          />
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => handleUpdateLink(link.id)}
                            className="me-1"
                          >
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="falcon-default"
                            onClick={() => setEditingLinkId(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="text-truncate me-2">
                            <a
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="fs-10"
                            >
                              <FontAwesomeIcon
                                icon="external-link-alt"
                                className="me-1"
                              />
                              {link.label || link.url}
                            </a>
                          </div>
                          <div className="d-flex">
                            <Button
                              size="sm"
                              variant="link"
                              className="text-500 p-0 me-2"
                              onClick={() => {
                                setEditingLinkId(link.id);
                                setEditLinkUrl(link.url);
                                setEditLinkLabel(link.label || "");
                              }}
                            >
                              <FontAwesomeIcon icon="edit" />
                            </Button>
                            <Button
                              size="sm"
                              variant="link"
                              className="text-danger p-0"
                              onClick={() => {
                                setLinkToDelete(link.id);
                                setDeleteLinkModal(true);
                              }}
                            >
                              <FontAwesomeIcon icon="trash-alt" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>

          {/* Actions */}
          <Card>
            <Card.Body>
              <Button
                variant="falcon-primary"
                size="sm"
                className="w-100 mb-2"
                onClick={() =>
                  navigate(
                    paths.projectTaskEdit(
                      projectId,
                      iterationId || "_",
                      taskId
                    )
                  )
                }
              >
                <FontAwesomeIcon icon="edit" className="me-1" />
                Edit Task
              </Button>
              <Button
                variant="falcon-default"
                size="sm"
                className="w-100"
                onClick={() => navigate(-1)}
              >
                <FontAwesomeIcon icon="arrow-left" className="me-1" />
                Go Back
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Delete Modals */}
      <ConfirmDeleteModal
        show={deleteCommentModal}
        setShow={setDeleteCommentModal}
        onConfirm={handleDeleteComment}
        itemName="comment"
        loading={deleteLoading}
      />
      <ConfirmDeleteModal
        show={deleteLinkModal}
        setShow={setDeleteLinkModal}
        onConfirm={handleDeleteLink}
        itemName="link"
        loading={deleteLoading}
      />
    </>
  );
};

export default TaskDetailPage;
